import type { CSSProperties } from 'react';
import type { PanelProduct } from '@storeo/api-client';
import s from './storePreview.module.css';

export interface PreviewSection {
  key: string;
  visible: boolean;
}

export interface PreviewDraft {
  name: string;
  tagline: string;
  primaryColor: string;
  accentColor: string;
  logoUrl: string | null;
  sections: PreviewSection[];
  testimonialQuote: string;
  testimonialAuthor: string;
}

function money(v: number): string {
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function visible(draft: PreviewDraft, key: string): boolean {
  return draft.sections.find((s) => s.key === key)?.visible ?? true;
}

/** Preview estilizado (não um iframe do storefront de verdade — ADR 0018 §5) que reage ao
 *  estado do editor na hora, antes de qualquer "Salvar". Porta o `.store` do artefato P9. */
export function StorePreview({
  draft,
  products,
  device,
}: {
  draft: PreviewDraft;
  products: PanelProduct[];
  device: 'desktop' | 'mobile';
}) {
  const published = products.filter((p) => p.status === 'published');
  const feature = published.find((p) => p.featured) ?? published[0] ?? null;
  const grid = (feature ? published.filter((p) => p !== feature) : published).slice(0, 3);

  const style = {
    '--pv-primary': draft.primaryColor,
    '--pv-accent': draft.accentColor,
  } as CSSProperties;

  return (
    <div className={s.store} data-dev={device} style={style}>
      <div className={s.sHead}>
        {draft.logoUrl ? (
          <img className={s.lg} src={draft.logoUrl} alt="" />
        ) : (
          <span className={s.lg} aria-hidden />
        )}
        <b>{draft.name || 'Sua loja'}</b>
      </div>

      <div className={s.sHero}>
        <h1>{draft.name || 'Sua loja'}</h1>
        {draft.tagline ? <p>{draft.tagline}</p> : null}
      </div>

      {visible(draft, 'feat') && feature ? (
        <div className={s.feat}>
          <div className={s.featIm} aria-hidden />
          <div className={s.featBody}>
            <span className={s.rib}>Mais vendido</span>
            <h3>{feature.name}</h3>
            {feature.description ? <p className={s.p}>{feature.description}</p> : null}
            <span className={s.buy}>Comprar · {money(feature.amount)}</span>
          </div>
        </div>
      ) : null}

      {visible(draft, 'grid') ? (
        <>
          <p className={s.label}>Produtos</p>
          {grid.length === 0 ? (
            <p className={s.p}>Seus produtos aparecem aqui.</p>
          ) : (
            <div className={s.grid}>
              {grid.map((p) => (
                <div key={p.id} className={s.pc}>
                  <div className={s.pcIm} aria-hidden />
                  <div className={s.pcBody}>
                    <h4>{p.name}</h4>
                    <div className={s.pr}>{money(p.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}

      {visible(draft, 'quote') && draft.testimonialQuote ? (
        <div className={s.quote}>
          “{draft.testimonialQuote}”{draft.testimonialAuthor ? <> — {draft.testimonialAuthor}</> : null}
        </div>
      ) : null}

      <div className={s.foot}>
        Vendido por {draft.name || 'sua loja'} · pagamento processado por Storeo
      </div>
    </div>
  );
}
